/**
 * Metric Registry — single source for every Group A metric.
 * id · label · unit · formatter · source · derived formula · tooltip · flags
 */

export const METRIC_KIND = {
  ACTUAL: "actual",
  ESTIMATE: "estimate",
  DERIVED: "derived",
  USER_CUSTOM: "user_custom",
};

export const METRIC_UNITS = {
  CURRENCY: "currency",
  PERCENT: "percent",
  RATIO: "ratio",
  SHARES: "shares",
  COUNT: "count",
};

/** @type {Record<string, object>} */
export const METRIC_REGISTRY = {
  revenue: {
    id: "revenue",
    label: "Revenue",
    unit: METRIC_UNITS.CURRENCY,
    formatter: "currency",
    source: { module: "income_statement", endpoint: "income-statement", fields: ["revenue"] },
    derivedFormula: null,
    tooltipId: "revenue",
    kind: METRIC_KIND.ACTUAL,
    hasEstimate: true,
    estimateFields: ["revenue_avg", "estimated_revenue_avg", "revenueAvg"],
    isPercent: false,
  },
  gross_profit: {
    id: "gross_profit",
    label: "Gross Profit",
    unit: METRIC_UNITS.CURRENCY,
    formatter: "currency",
    source: { module: "income_statement", endpoint: "income-statement", fields: ["grossProfit", "gross_profit"] },
    derivedFormula: "revenue − costOfRevenue",
    tooltipId: "gross_profit",
    kind: METRIC_KIND.ACTUAL,
    hasEstimate: true,
    estimateFields: ["gross_profit_avg"],
    isPercent: false,
  },
  operating_income: {
    id: "operating_income",
    label: "Operating Income",
    unit: METRIC_UNITS.CURRENCY,
    formatter: "currency",
    source: { module: "income_statement", endpoint: "income-statement", fields: ["operatingIncome", "operating_income"] },
    derivedFormula: null,
    tooltipId: "operating_income",
    kind: METRIC_KIND.ACTUAL,
    hasEstimate: true,
    estimateFields: ["operating_income_avg", "ebitda_avg"],
    isPercent: false,
  },
  net_income: {
    id: "net_income",
    label: "Net Income",
    unit: METRIC_UNITS.CURRENCY,
    formatter: "currency",
    source: { module: "income_statement", endpoint: "income-statement", fields: ["netIncome", "net_income"] },
    derivedFormula: null,
    tooltipId: "net_income",
    kind: METRIC_KIND.ACTUAL,
    hasEstimate: true,
    estimateFields: ["net_income_avg"],
    isPercent: false,
  },
  eps_diluted: {
    id: "eps_diluted",
    label: "EPS (diluted)",
    unit: METRIC_UNITS.CURRENCY,
    formatter: "perShare",
    source: { module: "income_statement", endpoint: "income-statement", fields: ["epsDiluted", "eps_diluted", "eps"] },
    derivedFormula: "netIncome ÷ weightedAverageShsOutDil",
    tooltipId: "eps_diluted",
    kind: METRIC_KIND.ACTUAL,
    hasEstimate: true,
    estimateFields: ["eps_avg", "estimated_eps_avg", "epsAvg"],
    isPercent: false,
    perShare: true,
  },
  ebitda: {
    id: "ebitda",
    label: "EBITDA",
    unit: METRIC_UNITS.CURRENCY,
    formatter: "currency",
    source: { module: "income_statement", endpoint: "income-statement", fields: ["ebitda"] },
    derivedFormula: null,
    tooltipId: "ebitda",
    kind: METRIC_KIND.ACTUAL,
    hasEstimate: true,
    estimateFields: ["ebitda_avg"],
    isPercent: false,
  },
  operating_cash_flow: {
    id: "operating_cash_flow",
    label: "Operating Cash Flow",
    unit: METRIC_UNITS.CURRENCY,
    formatter: "currency",
    source: { module: "cash_flow", endpoint: "cashflow-statement", fields: ["operatingCashFlow", "operating_cash_flow"] },
    derivedFormula: null,
    tooltipId: "operating_cash_flow",
    kind: METRIC_KIND.ACTUAL,
    hasEstimate: false,
    estimateFields: [],
    isPercent: false,
  },
  capital_expenditure: {
    id: "capital_expenditure",
    label: "Capex",
    unit: METRIC_UNITS.CURRENCY,
    formatter: "currency",
    source: { module: "cash_flow", endpoint: "cashflow-statement", fields: ["capitalExpenditure", "capital_expenditure"] },
    derivedFormula: null,
    tooltipId: "capital_expenditure",
    kind: METRIC_KIND.ACTUAL,
    hasEstimate: false,
    estimateFields: [],
    isPercent: false,
  },
  free_cash_flow: {
    id: "free_cash_flow",
    label: "Free Cash Flow",
    unit: METRIC_UNITS.CURRENCY,
    formatter: "currency",
    source: { module: "cash_flow", endpoint: "cashflow-statement", fields: ["freeCashFlow", "free_cash_flow"] },
    derivedFormula: "operatingCashFlow + capitalExpenditure",
    tooltipId: "free_cash_flow",
    kind: METRIC_KIND.DERIVED,
    hasEstimate: true,
    estimateFields: ["free_cash_flow_avg"],
    isPercent: false,
  },
  fcf_margin: {
    id: "fcf_margin",
    label: "FCF Margin",
    unit: METRIC_UNITS.PERCENT,
    formatter: "percent",
    source: { module: "income_statement", endpoint: "derived", fields: ["free_cash_flow", "revenue"] },
    derivedFormula: "freeCashFlow ÷ revenue × 100",
    tooltipId: "fcf_margin",
    kind: METRIC_KIND.DERIVED,
    hasEstimate: false,
    estimateFields: [],
    isPercent: true,
    derived: true,
  },
  fcf_per_share: {
    id: "fcf_per_share",
    label: "FCF / Share",
    unit: METRIC_UNITS.CURRENCY,
    formatter: "perShare",
    source: { module: "derived", endpoint: "derived", fields: ["free_cash_flow", "weightedAverageShsOutDil"] },
    derivedFormula: "freeCashFlow ÷ weightedAverageShsOutDil",
    tooltipId: "fcf_per_share",
    kind: METRIC_KIND.DERIVED,
    hasEstimate: false,
    estimateFields: [],
    isPercent: false,
    perShare: true,
    derived: true,
  },
  custom_fcf: {
    id: "custom_fcf",
    label: "Custom FCF",
    unit: METRIC_UNITS.CURRENCY,
    formatter: "currency",
    source: { module: "derived", endpoint: "derived", fields: ["operating_cash_flow", "capital_expenditure"] },
    derivedFormula: "operatingCashFlow − |capex| − stockBasedCompensation",
    tooltipId: "custom_fcf",
    kind: METRIC_KIND.USER_CUSTOM,
    hasEstimate: false,
    estimateFields: [],
    isPercent: false,
    displayOnly: true,
    derived: true,
  },
  gross_margin: {
    id: "gross_margin",
    label: "Gross Margin",
    unit: METRIC_UNITS.PERCENT,
    formatter: "percent",
    source: { module: "income_statement", endpoint: "derived", fields: ["gross_profit", "revenue"] },
    derivedFormula: "grossProfit ÷ revenue × 100",
    tooltipId: "gross_margin",
    kind: METRIC_KIND.DERIVED,
    hasEstimate: false,
    estimateFields: [],
    isPercent: true,
    derived: true,
  },
  operating_margin: {
    id: "operating_margin",
    label: "Operating Margin",
    unit: METRIC_UNITS.PERCENT,
    formatter: "percent",
    source: { module: "income_statement", endpoint: "derived", fields: ["operating_income", "revenue"] },
    derivedFormula: "operatingIncome ÷ revenue × 100",
    tooltipId: "operating_margin",
    kind: METRIC_KIND.DERIVED,
    hasEstimate: false,
    estimateFields: [],
    isPercent: true,
    derived: true,
  },
  net_margin: {
    id: "net_margin",
    label: "Net Margin",
    unit: METRIC_UNITS.PERCENT,
    formatter: "percent",
    source: { module: "income_statement", endpoint: "derived", fields: ["net_income", "revenue"] },
    derivedFormula: "netIncome ÷ revenue × 100",
    tooltipId: "net_margin",
    kind: METRIC_KIND.DERIVED,
    hasEstimate: false,
    estimateFields: [],
    isPercent: true,
    derived: true,
  },
  roic: {
    id: "roic",
    label: "ROIC",
    unit: METRIC_UNITS.PERCENT,
    formatter: "percent",
    source: { module: "key_metrics", endpoint: "key-metrics", fields: ["returnOnInvestedCapital"] },
    derivedFormula: "returnOnInvestedCapital × 100 when ≤ 1",
    tooltipId: "roic",
    kind: METRIC_KIND.ACTUAL,
    hasEstimate: false,
    estimateFields: [],
    isPercent: true,
  },
  roce: {
    id: "roce",
    label: "ROCE",
    unit: METRIC_UNITS.PERCENT,
    formatter: "percent",
    source: { module: "key_metrics", endpoint: "key-metrics", fields: ["returnOnCapitalEmployed"] },
    derivedFormula: "returnOnCapitalEmployed × 100 when ≤ 1",
    tooltipId: "roce",
    kind: METRIC_KIND.ACTUAL,
    hasEstimate: false,
    estimateFields: [],
    isPercent: true,
  },
  roe: {
    id: "roe",
    label: "ROE",
    unit: METRIC_UNITS.PERCENT,
    formatter: "percent",
    source: { module: "key_metrics", endpoint: "key-metrics", fields: ["returnOnEquity"] },
    derivedFormula: "returnOnEquity × 100 when ≤ 1",
    tooltipId: "roe",
    kind: METRIC_KIND.ACTUAL,
    hasEstimate: false,
    estimateFields: [],
    isPercent: true,
  },
  roa: {
    id: "roa",
    label: "ROA",
    unit: METRIC_UNITS.PERCENT,
    formatter: "percent",
    source: { module: "key_metrics", endpoint: "key-metrics", fields: ["returnOnAssets"] },
    derivedFormula: "returnOnAssets × 100 when ≤ 1",
    tooltipId: "roa",
    kind: METRIC_KIND.ACTUAL,
    hasEstimate: false,
    estimateFields: [],
    isPercent: true,
  },
  fcf_conversion: {
    id: "fcf_conversion",
    label: "FCF Conversion",
    unit: METRIC_UNITS.PERCENT,
    formatter: "percent",
    source: { module: "derived", endpoint: "derived", fields: ["free_cash_flow", "net_income"] },
    derivedFormula: "freeCashFlow ÷ netIncome × 100",
    tooltipId: "fcf_conversion",
    kind: METRIC_KIND.DERIVED,
    hasEstimate: false,
    estimateFields: [],
    isPercent: true,
    derived: true,
  },
  reinvestment_rate: {
    id: "reinvestment_rate",
    label: "Reinvestment Rate",
    unit: METRIC_UNITS.PERCENT,
    formatter: "percent",
    source: { module: "derived", endpoint: "derived", fields: ["capital_expenditure", "research_and_development_expenses", "revenue"] },
    derivedFormula: "(|capex| + R&D) ÷ revenue × 100",
    tooltipId: "reinvestment_rate",
    kind: METRIC_KIND.DERIVED,
    hasEstimate: false,
    estimateFields: [],
    isPercent: true,
    derived: true,
  },
  rnd_pct_revenue: {
    id: "rnd_pct_revenue",
    label: "R&D % Revenue",
    unit: METRIC_UNITS.PERCENT,
    formatter: "percent",
    source: { module: "income_statement", endpoint: "income-statement", fields: ["researchAndDevelopmentExpenses", "revenue"] },
    derivedFormula: "R&D ÷ revenue × 100",
    tooltipId: "rnd_pct_revenue",
    kind: METRIC_KIND.DERIVED,
    hasEstimate: false,
    estimateFields: [],
    isPercent: true,
    derived: true,
  },
  sga_pct_revenue: {
    id: "sga_pct_revenue",
    label: "S&M / G&A % Revenue",
    unit: METRIC_UNITS.PERCENT,
    formatter: "percent",
    source: { module: "income_statement", endpoint: "income-statement", fields: ["sellingGeneralAndAdministrativeExpenses", "revenue"] },
    derivedFormula: "SG&A ÷ revenue × 100",
    tooltipId: "sga_pct_revenue",
    kind: METRIC_KIND.DERIVED,
    hasEstimate: false,
    estimateFields: [],
    isPercent: true,
    derived: true,
  },
  cash: {
    id: "cash",
    label: "Cash",
    unit: METRIC_UNITS.CURRENCY,
    formatter: "currency",
    source: { module: "balance_sheet", endpoint: "balance-sheet", fields: ["cashAndCashEquivalents"] },
    derivedFormula: null,
    tooltipId: "cash",
    kind: METRIC_KIND.ACTUAL,
    hasEstimate: false,
    estimateFields: [],
    isPercent: false,
  },
  debt: {
    id: "debt",
    label: "Debt",
    unit: METRIC_UNITS.CURRENCY,
    formatter: "currency",
    source: { module: "balance_sheet", endpoint: "balance-sheet", fields: ["totalDebt", "longTermDebt"] },
    derivedFormula: null,
    tooltipId: "debt",
    kind: METRIC_KIND.ACTUAL,
    hasEstimate: false,
    estimateFields: [],
    isPercent: false,
  },
  net_debt: {
    id: "net_debt",
    label: "Net Debt",
    unit: METRIC_UNITS.CURRENCY,
    formatter: "currency",
    source: { module: "balance_sheet", endpoint: "derived", fields: ["totalDebt", "cashAndCashEquivalents"] },
    derivedFormula: "totalDebt − cash",
    tooltipId: "net_debt",
    kind: METRIC_KIND.DERIVED,
    hasEstimate: false,
    estimateFields: [],
    isPercent: false,
    derived: true,
  },
  net_debt_to_ebitda: {
    id: "net_debt_to_ebitda",
    label: "Net Debt / EBITDA",
    unit: METRIC_UNITS.RATIO,
    formatter: "ratio",
    source: { module: "key_metrics", endpoint: "key-metrics", fields: ["netDebtToEBITDA"] },
    derivedFormula: null,
    tooltipId: "net_debt_to_ebitda",
    kind: METRIC_KIND.ACTUAL,
    hasEstimate: false,
    estimateFields: [],
    isPercent: false,
  },
  interest_coverage: {
    id: "interest_coverage",
    label: "Interest Coverage",
    unit: METRIC_UNITS.RATIO,
    formatter: "ratio",
    source: { module: "key_metrics", endpoint: "key-metrics", fields: ["interestCoverage", "interestCoverageRatio"] },
    derivedFormula: null,
    tooltipId: "interest_coverage",
    kind: METRIC_KIND.ACTUAL,
    hasEstimate: false,
    estimateFields: [],
    isPercent: false,
  },
  piotroski_score: {
    id: "piotroski_score",
    label: "Piotroski F-Score",
    unit: METRIC_UNITS.COUNT,
    formatter: "count",
    source: { module: "financial_scores", endpoint: "financial-scores", fields: ["piotroskiScore", "piotroskiFScore"] },
    derivedFormula: null,
    tooltipId: "piotroski_score",
    kind: METRIC_KIND.ACTUAL,
    hasEstimate: false,
    estimateFields: [],
    isPercent: false,
  },
  altman_z_score: {
    id: "altman_z_score",
    label: "Altman Z-Score",
    unit: METRIC_UNITS.RATIO,
    formatter: "ratio",
    source: { module: "financial_scores", endpoint: "financial-scores", fields: ["altmanZScore"] },
    derivedFormula: null,
    tooltipId: "altman_z_score",
    kind: METRIC_KIND.ACTUAL,
    hasEstimate: false,
    estimateFields: [],
    isPercent: false,
  },
  revenue_growth: {
    id: "revenue_growth",
    label: "Revenue Growth",
    unit: METRIC_UNITS.PERCENT,
    formatter: "percent",
    source: { module: "growth", endpoint: "financial-growth", fields: ["revenueGrowth", "revenue_growth"] },
    derivedFormula: null,
    tooltipId: "revenue_growth",
    kind: METRIC_KIND.ACTUAL,
    hasEstimate: false,
    estimateFields: [],
    isPercent: true,
  },
  eps_growth: {
    id: "eps_growth",
    label: "EPS Growth",
    unit: METRIC_UNITS.PERCENT,
    formatter: "percent",
    source: { module: "growth", endpoint: "financial-growth", fields: ["epsDilutedGrowth", "eps_growth"] },
    derivedFormula: null,
    tooltipId: "eps_growth",
    kind: METRIC_KIND.ACTUAL,
    hasEstimate: false,
    estimateFields: [],
    isPercent: true,
  },
  fcf_growth: {
    id: "fcf_growth",
    label: "FCF Growth",
    unit: METRIC_UNITS.PERCENT,
    formatter: "percent",
    source: { module: "growth", endpoint: "financial-growth", fields: ["freeCashFlowGrowth", "free_cash_flow_growth"] },
    derivedFormula: null,
    tooltipId: "fcf_growth",
    kind: METRIC_KIND.ACTUAL,
    hasEstimate: false,
    estimateFields: [],
    isPercent: true,
  },
  gross_profit_growth: {
    id: "gross_profit_growth",
    label: "Gross Profit Growth",
    unit: METRIC_UNITS.PERCENT,
    formatter: "percent",
    source: { module: "growth", endpoint: "financial-growth", fields: ["grossProfitGrowth"] },
    derivedFormula: null,
    tooltipId: "gross_profit_growth",
    kind: METRIC_KIND.ACTUAL,
    hasEstimate: false,
    estimateFields: [],
    isPercent: true,
  },
  operating_income_growth: {
    id: "operating_income_growth",
    label: "Operating Income Growth",
    unit: METRIC_UNITS.PERCENT,
    formatter: "percent",
    source: { module: "growth", endpoint: "financial-growth", fields: ["operatingIncomeGrowth"] },
    derivedFormula: null,
    tooltipId: "operating_income_growth",
    kind: METRIC_KIND.ACTUAL,
    hasEstimate: false,
    estimateFields: [],
    isPercent: true,
  },
  revenue_per_share: {
    id: "revenue_per_share",
    label: "Revenue / Share",
    unit: METRIC_UNITS.CURRENCY,
    formatter: "perShare",
    source: { module: "ratios", endpoint: "metrics-ratios", fields: ["revenuePerShare"] },
    derivedFormula: null,
    tooltipId: "revenue_per_share",
    kind: METRIC_KIND.ACTUAL,
    hasEstimate: false,
    estimateFields: [],
    isPercent: false,
    perShare: true,
  },
  book_value_per_share: {
    id: "book_value_per_share",
    label: "Book Value / Share",
    unit: METRIC_UNITS.CURRENCY,
    formatter: "perShare",
    source: { module: "ratios", endpoint: "metrics-ratios", fields: ["bookValuePerShare"] },
    derivedFormula: null,
    tooltipId: "book_value_per_share",
    kind: METRIC_KIND.ACTUAL,
    hasEstimate: false,
    estimateFields: [],
    isPercent: false,
    perShare: true,
  },
  eps_revision_trend: {
    id: "eps_revision_trend",
    label: "EPS Revision Trend",
    unit: METRIC_UNITS.PERCENT,
    formatter: "percent",
    source: { module: "estimates", endpoint: "analyst-estimates", fields: ["epsAvg"] },
    derivedFormula: "period-over-period change in consensus epsAvg",
    tooltipId: "eps_revision_trend",
    kind: METRIC_KIND.ESTIMATE,
    hasEstimate: true,
    estimateFields: [],
    isPercent: true,
  },
  trailing_pe: {
    id: "trailing_pe",
    label: "Trailing P/E",
    unit: METRIC_UNITS.RATIO,
    formatter: "ratio",
    source: { module: "ratios", endpoint: "metrics-ratios", fields: ["priceToEarningsRatio"] },
    derivedFormula: null,
    tooltipId: "trailing_pe",
    kind: METRIC_KIND.ACTUAL,
    hasEstimate: false,
    estimateFields: [],
    isPercent: false,
  },
  forward_pe: {
    id: "forward_pe",
    label: "Forward P/E (live)",
    unit: METRIC_UNITS.RATIO,
    formatter: "ratio",
    source: { module: "quote", endpoint: "quote+estimates", fields: ["price", "epsAvg"] },
    derivedFormula: "quote.price ÷ next analyst epsAvg",
    tooltipId: "forward_pe",
    kind: METRIC_KIND.DERIVED,
    hasEstimate: true,
    estimateFields: [],
    isPercent: false,
    derived: true,
  },
  ev_to_sales: {
    id: "ev_to_sales",
    label: "EV / Sales",
    unit: METRIC_UNITS.RATIO,
    formatter: "ratio",
    source: { module: "key_metrics", endpoint: "key-metrics", fields: ["evToSales"] },
    derivedFormula: null,
    tooltipId: "ev_to_sales",
    kind: METRIC_KIND.ACTUAL,
    hasEstimate: false,
    estimateFields: [],
    isPercent: false,
  },
  ev_to_ebitda: {
    id: "ev_to_ebitda",
    label: "EV / EBITDA",
    unit: METRIC_UNITS.RATIO,
    formatter: "ratio",
    source: { module: "key_metrics", endpoint: "key-metrics", fields: ["evToEBITDA"] },
    derivedFormula: null,
    tooltipId: "ev_to_ebitda",
    kind: METRIC_KIND.ACTUAL,
    hasEstimate: false,
    estimateFields: [],
    isPercent: false,
  },
  price_to_free_cash_flow: {
    id: "price_to_free_cash_flow",
    label: "P / FCF",
    unit: METRIC_UNITS.RATIO,
    formatter: "ratio",
    source: { module: "ratios", endpoint: "metrics-ratios", fields: ["priceToFreeCashFlowRatio"] },
    derivedFormula: null,
    tooltipId: "price_to_free_cash_flow",
    kind: METRIC_KIND.ACTUAL,
    hasEstimate: false,
    estimateFields: [],
    isPercent: false,
  },
  fcf_yield: {
    id: "fcf_yield",
    label: "FCF Yield",
    unit: METRIC_UNITS.PERCENT,
    formatter: "percent",
    source: { module: "key_metrics", endpoint: "key-metrics", fields: ["freeCashFlowYield"] },
    derivedFormula: null,
    tooltipId: "fcf_yield",
    kind: METRIC_KIND.ACTUAL,
    hasEstimate: false,
    estimateFields: [],
    isPercent: true,
  },
  earnings_yield: {
    id: "earnings_yield",
    label: "Earnings Yield",
    unit: METRIC_UNITS.PERCENT,
    formatter: "percent",
    source: { module: "key_metrics", endpoint: "key-metrics", fields: ["earningsYield"] },
    derivedFormula: null,
    tooltipId: "earnings_yield",
    kind: METRIC_KIND.ACTUAL,
    hasEstimate: false,
    estimateFields: [],
    isPercent: true,
  },
  peg: {
    id: "peg",
    label: "PEG",
    unit: METRIC_UNITS.RATIO,
    formatter: "ratio",
    source: { module: "ratios", endpoint: "metrics-ratios", fields: ["priceToEarningsGrowthRatio"] },
    derivedFormula: null,
    tooltipId: "peg",
    kind: METRIC_KIND.ACTUAL,
    hasEstimate: false,
    estimateFields: [],
    isPercent: false,
  },
  shares_outstanding: {
    id: "shares_outstanding",
    label: "Shares Outstanding",
    unit: METRIC_UNITS.SHARES,
    formatter: "shares",
    source: {
      module: "income_statement",
      endpoint: "income-statement",
      fields: ["weightedAverageShsOutDil", "weightedAverageShsOut"],
    },
    derivedFormula: null,
    tooltipId: "shares_outstanding",
    kind: METRIC_KIND.ACTUAL,
    hasEstimate: false,
    estimateFields: [],
    isPercent: false,
  },
  stock_based_compensation: {
    id: "stock_based_compensation",
    label: "Stock-Based Compensation",
    unit: METRIC_UNITS.CURRENCY,
    formatter: "currency",
    source: { module: "cash_flow", endpoint: "cashflow-statement", fields: ["stockBasedCompensation"] },
    derivedFormula: null,
    tooltipId: "stock_based_compensation",
    kind: METRIC_KIND.ACTUAL,
    hasEstimate: false,
    estimateFields: [],
    isPercent: false,
  },
  share_buybacks: {
    id: "share_buybacks",
    label: "Share Buybacks",
    unit: METRIC_UNITS.CURRENCY,
    formatter: "currency",
    source: { module: "cash_flow", endpoint: "cashflow-statement", fields: ["commonStockRepurchased"] },
    derivedFormula: null,
    tooltipId: "share_buybacks",
    kind: METRIC_KIND.ACTUAL,
    hasEstimate: false,
    estimateFields: [],
    isPercent: false,
  },
  dividends_paid: {
    id: "dividends_paid",
    label: "Dividends Paid",
    unit: METRIC_UNITS.CURRENCY,
    formatter: "currency",
    source: { module: "cash_flow", endpoint: "cashflow-statement", fields: ["commonDividendsPaid"] },
    derivedFormula: null,
    tooltipId: "dividends_paid",
    kind: METRIC_KIND.ACTUAL,
    hasEstimate: false,
    estimateFields: [],
    isPercent: false,
  },
  acquisitions_net: {
    id: "acquisitions_net",
    label: "Acquisitions (Net)",
    unit: METRIC_UNITS.CURRENCY,
    formatter: "currency",
    source: { module: "cash_flow", endpoint: "cashflow-statement", fields: ["acquisitionsNet"] },
    derivedFormula: null,
    tooltipId: "acquisitions_net",
    kind: METRIC_KIND.ACTUAL,
    hasEstimate: false,
    estimateFields: [],
    isPercent: false,
  },
  growth_vs_pe: {
    id: "growth_vs_pe",
    label: "Growth vs P/E",
    unit: METRIC_UNITS.RATIO,
    formatter: "ratio",
    source: { module: "derived", endpoint: "derived", fields: ["eps_growth", "trailing_pe"] },
    derivedFormula: "EPS growth vs trailing P/E by period",
    tooltipId: "growth_vs_pe",
    kind: METRIC_KIND.DERIVED,
    hasEstimate: false,
    estimateFields: [],
    isPercent: false,
    derived: true,
  },
  price_vs_fcf_growth: {
    id: "price_vs_fcf_growth",
    label: "Price vs FCF Growth",
    unit: METRIC_UNITS.RATIO,
    formatter: "ratio",
    source: { module: "derived", endpoint: "derived", fields: ["price", "free_cash_flow"] },
    derivedFormula: "Indexed price vs indexed FCF",
    tooltipId: "price_vs_fcf_growth",
    kind: METRIC_KIND.DERIVED,
    hasEstimate: false,
    estimateFields: [],
    isPercent: false,
    derived: true,
  },
  price: {
    id: "price",
    label: "Price",
    unit: METRIC_UNITS.CURRENCY,
    formatter: "currency",
    source: { module: "price_history", endpoint: "historical-price", fields: ["close", "price"] },
    derivedFormula: null,
    tooltipId: "price",
    kind: METRIC_KIND.ACTUAL,
    hasEstimate: false,
    estimateFields: [],
    isPercent: false,
  },
  dma200: {
    id: "dma200",
    label: "200DMA",
    unit: METRIC_UNITS.CURRENCY,
    formatter: "currency",
    source: { module: "price_history", endpoint: "derived", fields: ["close"] },
    derivedFormula: "200-day average of close",
    tooltipId: "dma200",
    kind: METRIC_KIND.DERIVED,
    hasEstimate: false,
    estimateFields: [],
    isPercent: false,
    derived: true,
  },
  drawdown_pct: {
    id: "drawdown_pct",
    label: "Drawdown",
    unit: METRIC_UNITS.PERCENT,
    formatter: "percent",
    source: { module: "price_history", endpoint: "derived", fields: ["close"] },
    derivedFormula: "Peak-to-trough % from historical close",
    tooltipId: "drawdown_pct",
    kind: METRIC_KIND.DERIVED,
    hasEstimate: false,
    estimateFields: [],
    isPercent: true,
    derived: true,
  },
  rsi_14: {
    id: "rsi_14",
    label: "RSI (14)",
    unit: METRIC_UNITS.RATIO,
    formatter: "ratio",
    source: { module: "price_history", endpoint: "derived", fields: ["close"] },
    derivedFormula: "14-period RSI from close prices",
    tooltipId: "rsi_14",
    kind: METRIC_KIND.DERIVED,
    hasEstimate: false,
    estimateFields: [],
    isPercent: false,
    derived: true,
  },
  distance_from_ath: {
    id: "distance_from_ath",
    label: "Distance from ATH",
    unit: METRIC_UNITS.PERCENT,
    formatter: "percent",
    source: { module: "quote", endpoint: "quote", fields: ["yearHigh", "price"] },
    derivedFormula: "(ATH − price) ÷ ATH × 100",
    tooltipId: "distance_from_ath",
    kind: METRIC_KIND.DERIVED,
    hasEstimate: false,
    estimateFields: [],
    isPercent: true,
    derived: true,
  },
};

export function getMetric(metricId) {
  return METRIC_REGISTRY[metricId] || null;
}

export function buildEstimateFieldMap() {
  return Object.fromEntries(
    Object.values(METRIC_REGISTRY)
      .filter((m) => m.hasEstimate && m.estimateFields?.length)
      .map((m) => [m.id, m.estimateFields]),
  );
}
